import { type UserInfo } from 'firebase/auth'
import { type ApiToken } from '~/utils/apiToken'
import { type DocumentReference } from 'firebase/firestore'

export interface UserState {
	displayName: string
	email: string
	photoURL: string
	uid?: string
}

export type ProfileTag = 'player' | 'gm' | 'open-to-play' | 'open-to-gm';

export const PROFILE_TAGS: { value: ProfileTag; label: string }[] = [
	{ value: 'player', label: 'Player' },
	{ value: 'gm', label: 'GM' },
	{ value: 'open-to-play', label: 'Open to Play' },
	{ value: 'open-to-gm', label: 'Open to GM' },
];

/**
 * Per-operator visual theme. `undefined` keeps the current default
 * Cybercore palette (cyan-on-black). Any value here applies a
 * `theme-<id>` class on <html> at app load — see assets/css/themes.scss
 * for the palette overrides.
 *
 * Live preview pages: public/themes/<id>.html (1–10).
 */
export type ThemeId =
	| 'bunker-green'
	| 'corsec-blueprint'
	| 'rust-salvage'
	| 'plasma-cathode'
	| 'bone-lime'
	| 'mercs-combat-orange'
	| 'ice-boardroom'
	| 'saros-crimson'
	| 'grad-stencil'
	| 'nerd-lab-mint';

export const THEMES: { value: ThemeId; label: string; blurb: string }[] = [
	{ value: 'bunker-green',        label: '1 · Bunker Green',         blurb: 'CRT phosphor · black + lime + amber' },
	{ value: 'corsec-blueprint',    label: '2 · CORSEC Blueprint',     blurb: 'Navy · blueprint blue · cyan + amber' },
	{ value: 'rust-salvage',        label: '3 · Rust Salvage',         blurb: 'Dark brown · rust · cream' },
	{ value: 'plasma-cathode',      label: '4 · Plasma Cathode',       blurb: 'Purple · magenta · cyan + amber' },
	{ value: 'bone-lime',           label: '5 · Bone & Lime',          blurb: 'Near-black · bone · lime + soft blue' },
	{ value: 'mercs-combat-orange', label: '6 · MERCS Combat Orange',  blurb: 'Warm-black · combat orange · ivory' },
	{ value: 'ice-boardroom',       label: '7 · ICE Boardroom',        blurb: 'Ash grey · ice white · frost blue · serif' },
	{ value: 'saros-crimson',       label: '8 · SAROS Crimson',        blurb: 'Dark warm · emergency red · bone' },
	{ value: 'grad-stencil',        label: '9 · GRAD Stencil',         blurb: 'Penitentiary grey · safety orange · cream' },
	{ value: 'nerd-lab-mint',       label: '10 · NERD Lab Mint',       blurb: 'Light theme · bone paper · mint · charcoal' },
];

export interface UserProfile extends UserState {
	uid: string
	alias: string
	tokenRef?: DocumentReference | null
	tags: ProfileTag[]
	/** Selected theme. null = use the default Cybercore palette.
	 *  Stored as null rather than undefined because Firestore setDoc()
	 *  throws on undefined values, which silently fails the whole write. */
	theme?: ThemeId | null
	/** Text-size boost, as a whole-number percent (0–30). Applied site-wide
	 *  by scaling the root font-size to (1 + fontScale/100). 0/undefined =
	 *  default size. See assets/css/default.scss --cc-font-scale. */
	fontScale?: number
}

export interface UserPermissions {
	admin: boolean
	betaTester: boolean
}

export const userInfo2UserState = (user: UserInfo): UserState => {
	return {
		displayName: user.displayName || '',
		email: user.email || '',
		photoURL: user.photoURL ||'',
		uid: user.uid,
	}
}

export const defaultUser = (): UserState => {
	return {
		displayName: '',
		email: '',
		photoURL: '',
		uid: undefined,
	}
}

export const userState2Profile = (user: UserState): UserProfile => {
	return {
		displayName: user.displayName || '',
		email: user.email || '',
		photoURL: user.photoURL || '',
		uid: user.uid || '',
		alias: '',
		tokenRef: null,
		tags: ['player'],
		theme: null,
	}
}


export const defaultProfile = (): UserProfile => {
	let user: UserProfile = defaultUser() as UserProfile;
	user.uid = '';
	user.alias = '';
	user.tokenRef = null;
	user.tags = ['player'];
	user.theme = null;
	return user;
}

export const defaultPermissions = (): UserPermissions => {
	return {
		admin: false,
		betaTester: false,
	}
}
